import {
  LayoutDashboard,
  Users,
  UserCog,
  UserCircle,
  Building2,
  ShieldCheck,
  Megaphone,
  FileText,
  FileArchive,
  FileSpreadsheet,
  FileBox,
  IdCard,
  Layers3,
  CalendarDays,
  CalendarClock,
  Settings,
  Lock,
  Briefcase,
  UserCheck,
  UserPlus,
  ClipboardList,
  Wallet,
  DollarSign,
  Receipt,
  HandCoins,
  PlaneTakeoff,
  UserX,
  Inbox,
  FileSignature,
  GraduationCap,
  Calculator,
  Clock,
  Clipboard,
  Package,
  Boxes,
  Monitor,
  HardDrive,
  Server,
  BarChart3,
  LifeBuoy,
  Database,
  ArrowLeftRight,
  CreditCard,
  Target,
  ListChecks,
  TrendingUp,
  CheckCircle2,
  Plug,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { buildDynamicNav } from "../components/common/CommonFunctions";
import { isOrgOverview } from "../utils/branchSession";

// Custom event name dispatched by pages (Home, AdminDashboard) after they
// hydrate sessionStorage with customConfig/permissions so the sidebar can
// re-render without requiring a hard page refresh. Exported for callers.
export const SESSION_STORAGE_HYDRATED_EVENT = "hrms:session-storage-hydrated";

export const useNavigation = () => {

  // Force re-evaluation of the synchronous sessionStorage reads below when
  // another component finishes hydrating session storage and dispatches the
  // event. Without this, the sidebar mounts before the dashboard fetch
  // completes and ends up missing flag-gated items like Training.
  const [, setHydrationTick] = useState(0);
  useEffect(() => {
    const onHydrated = () => setHydrationTick((n) => n + 1);
    window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, onHydrated);
    // Cross-tab safety net — keep the sidebar in sync if customConfig changes
    // in another tab.
    window.addEventListener("storage", onHydrated);
    return () => {
      window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, onHydrated);
      window.removeEventListener("storage", onHydrated);
    };
  }, []);

  const userPermissionList = JSON.parse(sessionStorage.getItem("loginUserPermission")) || [];
  const is_superAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false")
  const is_Admin = JSON.parse(sessionStorage.getItem("is_admin") || "false")
  const is_systemAdmin = JSON.parse(sessionStorage.getItem("is_system_admin") || "false")
  const customConfig = JSON.parse(sessionStorage.getItem("customConfig")) || {};
  const isTrainingEnabled = !!customConfig.is_nabh;
  // Branch Transfer is only meaningful when the company has more than one active
  // branch, and is restricted to ORG ADMIN and BRANCH MANAGER (branch admin)
  // only — a plain employee never raises or approves transfers. multi_branch is
  // role-independent (2+ branches); the role check is what hides it from staff.
  // Independent of the is_branch_enabled toggle (that gates branch CRUD only).
  const isBranchAdmin = JSON.parse(sessionStorage.getItem("is_branch_admin") || "false");
  const multiBranch = JSON.parse(sessionStorage.getItem("multi_branch") || "false");
  const showBranchTransfer = multiBranch && (is_Admin || is_superAdmin || isBranchAdmin);
  // Set once an eMedHub connection is saved under Settings > Integrations
  // (see ConnectEmedHub.js). Gates the left-sidebar "Integration" item below.
  const emedhubConnected = JSON.parse(sessionStorage.getItem("emedhubConnected") || "false");

  const defaults = {
    employee: [
      "leave request",
      "reimbursement request",
      "advance salary request",
      "subordinates",
      "branch transfer",
      "resignation",
      "my timesheet",
      ...(is_superAdmin || is_Admin ? ["team timesheet"] : []),
    ],

    attendance: [
      "day attendance",
      "attendance log"
    ],

    appraisal: [
      "dashboard",
      "my appraisal",
      "team review",
      "kpi manager",
      "cycles",
      "final approval",
    ],

    // ignored: because we attached the salary calculator in salary remplate creation screen
    // payroll: [ 
    //   "salary calculator" 
    // ],

    ...(is_superAdmin || is_Admin ? {
      "data migration": ["employee migration"],
      ...(isTrainingEnabled ? { training: ["all trainings", "assign training", "overdue"] } : {}),
      // "ai copilot" is always available: the backend allow-lists the 'ai'
      // module for every logged-in employee and scopes each answer to the
      // caller, so there is no separate permission key to gate it on.
      fullMenus: ["reports", "settings", "help", "plans", "it help desk", "ai copilot",
        ...(emedhubConnected ? ["integration"] : [])],
    } : {
      fullMenus: ["settings", "it help desk", "ai copilot"],
    })
  };
  const navItems = [
    // Dashboard changes based on role
    { label: "Home", href: "/home", icon: LayoutDashboard },

    // Admin Panel (only for admin roles)
    {
      label: "Administrator",
      href: "/admin",
      icon: UserCog,
      subItems: [
        { label: "Organization", href: "/admin/companyprofile", icon: Building2 },
        { label: "User Group", href: "/admin/usergroup", icon: Users },
        { label: "User Permission", href: "/admin/userpermission", icon: ShieldCheck },
        { label: "Notice Board", href: "/admin/noticeboard", icon: Megaphone },
        { label: "Password Policy", href: "/admin/passwordpolicy", icon: Lock },
        { label: "Organization Documents", href: "/admin/organizationdocuments", icon: FileArchive },
      ],
    },

    // Master (Admin/Superadmin only)
    {
      label: "Master",
      href: "/master",
      icon: Layers3,
      subItems: [
        { label: "Employee Type", href: "/master/employeetype", icon: IdCard },
        { label: "Department", href: "/master/department", icon: Building2 },
        { label: "Designation", href: "/master/designation", icon: Briefcase },
        { label: "Leave Type", href: "/master/leavetype", icon: CalendarClock },
        { label: "Employee Group", href: "/master/employeegroup", icon: Users },
        { label: "Reimbursements Type", href: "/master/reimbursementstype", icon: Receipt },
        { label: "Asset Type", href: "/master/assettype", icon: Package },
        { label: "Asset Category", href: "/master/assetcategory", icon: Boxes },
        { label: "Asset Sub Category", href: "/master/assetsubategory", icon: FileBox },
        { label: "Letter Type", href: "/master/lettertype", icon: FileText },
        { label: "Letter Template", href: "/master/lettertemplate", icon: FileSignature },
      ],
    },

    // Configuration (Admin/Superadmin only)
    {
      label: "Configuration",
      href: "/configuration",
      icon: Settings,
      subItems: [
        { label: "Shift Configuration", href: "/configuration/shiftconfiguration", icon: CalendarClock },
        { label: "Holiday", href: "/configuration/holiday", icon: CalendarDays },
        { label: "Week off", href: "/configuration/weekoff", icon: Clipboard },
        { label: "Payroll Period", href: "/configuration/payrollperiod", icon: CalendarDays },
        { label: "Subordinate", href: "/configuration/subordinate", icon: UserCheck },
        { label: "Salary Component", href: "/configuration/salarycomponent", icon: Wallet },
        { label: "Salary Template", href: "/configuration/salarytemplate", icon: FileSpreadsheet },
      ],
    },

    // Employee Section
    {
      label: "Employee",
      href: "/employee",
      icon: UserCircle,
      subItems: [
        { label: "Employees", href: "/employee", icon: Users },
        showBranchTransfer ? { label: "Branch Transfer", href: "/employee/branch-transfer", icon: ArrowLeftRight } : null, // below Employees; only when the company has 2+ branches
        { label: "Leave Request", href: "/employee/leaverequest", icon: PlaneTakeoff }, // Default
        { label: "Reimbursement Request", href: "/employee/reimbursementrequest", icon: Receipt }, // Default
        { label: "Advance Salary Request", href: "/employee/advancerequest", icon: HandCoins }, // Default
        { label: "Subordinates", href: "/employee/subordinate", icon: UserCheck }, // Default
        { label: "Resignation", href: "/employee/resignationrequest", icon: UserX }, // Default
        { label: "Approval Inbox", href: "/employee/approvalinbox", icon: Inbox },
        // { label: "TDS Declaration Form", href: "/employee/tdsdeclaration", icon: FileText },
        { label: "Employee Onboarding", href: "/employee/onboarding", icon: UserPlus },
        { label: "Intern Onboarding", href: "/employee/internonboarding", icon: GraduationCap },
        { label: "My Timesheet", href: "/employee/timesheet", icon: Clock },
        { label: "Team Timesheet", href: "/employee/team/timesheet", icon: Clock },
      ].filter(Boolean), // clean nulls if condition fails
    },

    // Payroll (Admin/Superadmin only)
    {
      label: "Payroll",
      href: "/payroll",
      icon: DollarSign,
      subItems: [
        // { label: "Run Payroll", href: "/payroll/runpayroll", icon: ClipboardList },
        { label: "Payroll", href: "/payroll/payrollprocess", icon: FileText },
        { label: "Payslips", href: "/payroll/payslips", icon: FileSpreadsheet },
        // TDS: preview is the pre-payroll review gate, configuration holds the
        // per-financial-year slab grid.
        { label: "TDS Preview", href: "/payroll/tdspreview", icon: Receipt },
        { label: "TDS Configuration", href: "/payroll/tdsconfiguration", icon: Calculator },
        // { label: "Salary Calculator", href: "/payroll/salarycalcualtor", icon: Calculator }, ignored: because we attached the salary calculator in salary remplate creation screen 
      ],
    },

    // Attendance (All users, but different)
    {
      label: "Attendance",
      href: "/attendance",
      icon: Clock,
      subItems: [
        { label: "Day Attendance", href: "/attendance/dayattendance", icon: CalendarDays },
        { label: "Attendance Log", href: "/attendance/attendancelog", icon: ClipboardList },
      ],
    },

    // Asset Management (System admin / Superadmin only)
    (is_systemAdmin || is_superAdmin) && {
      label: "Asset Management",
      href: "/assetmanagement",
      icon: Monitor,
      subItems: [
        { label: "Asset Request", href: "/assetmanagement/assetrequest", icon: Inbox },
        { label: "Asset Registry", href: "/assetmanagement/assetregistry", icon: HardDrive },
        { label: "Asset Employee Audit", href: "/assetmanagement/assetemployeeaudit", icon: Server },
      ],
    },

    // Roster (everyone)
    { label: "Roster", href: "/roster", icon: CalendarClock },

    // Appraisal — sub-items are gated by role.
    // HR (is_admin/is_super_admin) gets cycles + final approval; RMs get team
    // review; everyone authenticated gets dashboard + my appraisal. KPI
    // Manager is shown to RM and HR.
    (() => {
      const reportingManagerRaw = sessionStorage.getItem("reportingManager");
      const isRM = reportingManagerRaw === "true" || reportingManagerRaw === true;
      const isHR = !!(is_Admin || is_superAdmin);
      const subItems = [
        { label: "Dashboard", href: "/appraisal", icon: LayoutDashboard },
        { label: "My Appraisal", href: "/appraisal/my-appraisal", icon: ClipboardList },
        isRM && { label: "Team Review", href: "/appraisal/team-review", icon: UserCheck },
        (isRM || isHR) && { label: "KPI Manager", href: "/appraisal/kpi-manager", icon: ListChecks },
        isHR && { label: "Cycles", href: "/appraisal/cycles", icon: CalendarClock },
        isHR && { label: "Final Approval", href: "/appraisal/final-approval", icon: CheckCircle2 },
      ].filter(Boolean);
      return {
        label: "Appraisal",
        href: "/appraisal",
        icon: Target,
        subItems,
      };
    })(),


    isTrainingEnabled && {
      label: "Training",
      href: "/training",
      icon: GraduationCap,
      subItems: (is_Admin || is_superAdmin)
        ? [
          { label: "All Trainings", href: "/training", icon: ClipboardList },
          { label: "Assign Training", href: "/training/add", icon: UserPlus },
          { label: "Overdue", href: "/training/overdue", icon: CalendarClock },
        ]
        : [
          { label: "My Trainings", href: "/training", icon: ClipboardList },
          { label: "Team Trainings", href: "/training/team", icon: UserCheck },
          { label: "Assign Training", href: "/training/add", icon: UserPlus },
        ],
    },

    {
      label: "Data Migration",
      href: "/datamigration",
      icon: Database,
      subItems: [
        { label: "Employee Migration", href: "/datamigration/employeemigration", icon: ArrowLeftRight },
      ],
    },

    // Reports (all, but richer for admin)
    {
      label: "Reports",
      href: "/reports",
      icon: BarChart3,
      isSecondary: true,
    },

    // Settings (admin only)
    {
      label: "Settings",
      href: "/settings",
      icon: Settings,
      isSecondary: true,
    },

    // eMedHub Integration — only after a connection is saved (gated via
    // defaults.fullMenus above using the emedhubConnected flag).
    {
      label: "Integration",
      href: "/integration",
      icon: Plug,
      isSecondary: true,
    },

    // { label: "Plans", href: "/plans", icon: CreditCard, isSecondary: true },

    // Help (everyone)
    { label: "IT Help Desk", href: "/ithelpdesk", icon: LifeBuoy, isSecondary: true },
      
    // AI Copilot — self-service for everyone (see defaults.fullMenus below).
    { label: "AI Copilot", href: "/ai/copilot", icon: Sparkles, isSecondary: true, },


  ].filter(Boolean); // remove false entries

  const dynamicNav = buildDynamicNav(navItems, userPermissionList, defaults);
  // const dynamicNav = navItems

  // Org admin in Dashboard Overview (all-branches) mode: restrict the sidebar to
  // Dashboard, Employee List, Reports and Settings only. Inside a specific
  // branch this is false and the normal permission-based menu (dynamicNav) is
  // used. Settings here is the curated 4-tab view (see SettingsLayout).
  if (isOrgOverview()) {
    const overviewNav = [
      { label: "Dashboard", href: "/home", icon: LayoutDashboard },
      {
        label: "Employee",
        href: "/employee",
        icon: UserCircle,
        subItems: [
          { label: "Employees", href: "/employee", icon: Users },
          // Overview is always multi-branch (branch_avail requires 2+ branches),
          // so Branch Transfer applies here.
          { label: "Branch Transfer", href: "/employee/branch-transfer", icon: ArrowLeftRight },
        ],
      },
      { label: "Reports", href: "/reports", icon: BarChart3, isSecondary: true },
      { label: "Settings", href: "/settings", icon: Settings, isSecondary: true },
    ];
    return { navItems, dynamicNav: overviewNav };
  }

  return { navItems, dynamicNav };
};
